'use client';

import { useState, FormEvent, useEffect } from 'react';
import { CatalogOrderData, Product } from '@/types/order.types';
import { orderService } from '@/services/orderService';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface ProductWithQuantity extends Product {
  quantity: number;
}

interface GroupedProducts {
  [category: string]: ProductWithQuantity[];
}

export default function OrderFormContent() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const [products, setProducts] = useState<ProductWithQuantity[]>([]);
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Load products on mount
  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setIsLoading(true);
    const { data, error } = await orderService.getAllProducts();
    
    if (error) {
      console.error('Failed to load products:', error);
      setErrorMessage('Failed to load products. Please refresh the page.');
      setIsLoading(false);
      return;
    }

    if (data) {
      const productsWithQuantity: ProductWithQuantity[] = data.map(p => ({
        ...p,
        quantity: 0
      }));
      setProducts(productsWithQuantity);
    }
    setIsLoading(false);
  };

  // Group products by category and sort alphabetically within each category
  const getGroupedProducts = (): GroupedProducts => {
    const categoryOrder = ['Structural', 'Power', 'Electrical', 'Cabs & Gates'];
    const grouped: GroupedProducts = {};

    // Initialize categories in the desired order
    categoryOrder.forEach(cat => {
      grouped[cat] = [];
    });

    // Group products by category
    products.forEach(product => {
      const category = product.category || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(product);
    });

    // Sort products alphabetically within each category by description
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) => 
        a.description.localeCompare(b.description)
      );
    });

    // Remove empty categories
    Object.keys(grouped).forEach(category => {
      if (grouped[category].length === 0) {
        delete grouped[category];
      }
    });

    return grouped;
  };

  const handleQuantityChange = (productId: string, quantity: string) => {
    const qty = parseInt(quantity) || 0;
    setProducts(prev => prev.map(p => 
      p.id === productId ? { ...p, quantity: Math.max(0, qty) } : p
    ));
  };

  const calculateLineTotal = (price: number, quantity: number): number => {
    return price * quantity;
  };

  const calculateGrandTotal = (): number => {
    return products.reduce((total, p) => {
      if (p.quantity > 0) {
        return total + (p.price * p.quantity);
      }
      return total;
    }, 0);
  };

  const getSelectedItems = () => {
    return products
      .filter(p => p.quantity > 0)
      .map(p => ({
        product_id: p.id,
        part_id: p.part_id,
        description: p.description,
        quantity: p.quantity,
        unit_price: p.price,
        line_total: calculateLineTotal(p.price, p.quantity)
      }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const selectedItems = getSelectedItems();
    
    if (selectedItems.length === 0) {
      setSubmitStatus('error');
      setErrorMessage('Please select at least one product with quantity greater than 0');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    const orderData: CatalogOrderData = {
      company_name: companyName,
      contact_name: contactName,
      email,
      phone,
      order_total: calculateGrandTotal(),
      notes: notes || undefined,
      items: selectedItems
    };

    try {
      const result = await orderService.submitCatalogOrder(orderData);

      if (result.success) {
        setSubmitStatus('success');
        setShowSuccessModal(true);
        
        // Reset form
        setCompanyName('');
        setContactName('');
        setEmail('');
        setPhone('');
        setNotes('');
        setProducts(prev => prev.map(p => ({ ...p, quantity: 0 })));
      } else {
        setSubmitStatus('error');
        setErrorMessage(result.error || 'Submission failed');
      }
    } catch (error: any) {
      setSubmitStatus('error');
      setErrorMessage(error?.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowSuccessModal(false);
    setSubmitStatus('idle');
  };

  const groupedProducts = getGroupedProducts();

  // Auth loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show login gate
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-8">
          <div>
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gray-100 mb-6">
              <svg className="h-10 w-10 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-3xl font-light tracking-tight text-gray-900 mb-3">
              Login Required
            </h2>
            <p className="text-gray-600 mb-2">
              Access to the order form and pricing is restricted to authorized customers.
            </p>
            <p className="text-sm text-gray-500 mb-8">
              Please sign in to view pricing and place orders.
            </p>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => router.push('/customer-login')}
              className="w-full py-3 px-4 bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors"
            >
              Sign In to Your Account
            </button>
            <p className="text-sm text-gray-500">
              Don&apos;t have an account?{' '}
              <a href="mailto:sales@crelevator.com" className="text-gray-900 font-medium hover:underline">
                Contact us
              </a>{' '}
              to get access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Success Modal Popup */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-8 max-w-md w-full mx-4 border-2 border-primary shadow-2xl">
            <div className="text-center">
              <div className="mb-6">
                <svg className="w-20 h-20 mx-auto text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              <h2 className="font-heading text-3xl font-bold text-text-primary mb-4">
                Congratulations!
              </h2>
              
              <p className="font-body text-lg text-text-secondary mb-6">
                You have submitted your order successfully.
              </p>
              
              <p className="font-body text-sm text-text-secondary mb-8">
                A confirmation email has been sent to your email address. Your Account Manager will follow up with the official order confirmation and estimated delivery date.
              </p>
              
              <button
                onClick={closeModal}
                className="px-8 py-3 bg-primary text-primary-foreground font-cta font-semibold text-sm tracking-wide border border-primary transition-all duration-fast hover:-translate-y-1 hover:bg-accent hover:border-accent"
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-white text-black py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl lg:text-5xl font-bold mb-4">
              Submit an Order
            </h1>
            <p className="font-body text-lg leading-relaxed opacity-90">
              Select products from our catalog below, enter quantities, and submit your order. Our team will review your order and contact you within 24 hours with a detailed quote.
            </p>
          </div>
        </div>
      </section>

      {/* Order Form Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {submitStatus === 'error' && (
            <div className="mb-8 p-6 bg-red-50 border border-red-200 text-red-800">
              <p className="font-cta font-semibold">
                ✗ {errorMessage || 'Something went wrong. Please try again or contact us directly.'}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-12">
            {/* Customer Information */}
            <div className="space-y-6">
              <h2 className="font-heading text-2xl font-bold text-text-primary border-b border-border pb-3">
                Customer Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block font-cta text-sm font-semibold text-text-primary mb-2">
                    COMPANY NAME *
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-border bg-background text-text-primary focus:border-primary focus:outline-none transition-colors duration-fast"
                  />
                </div>

                <div>
                  <label htmlFor="contactName" className="block font-cta text-sm font-semibold text-text-primary mb-2">
                    CONTACT NAME *
                  </label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-border bg-background text-text-primary focus:border-primary focus:outline-none transition-colors duration-fast"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block font-cta text-sm font-semibold text-text-primary mb-2">
                    EMAIL ADDRESS *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-border bg-background text-text-primary focus:border-primary focus:outline-none transition-colors duration-fast"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block font-cta text-sm font-semibold text-text-primary mb-2">
                    PHONE NUMBER *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-border bg-background text-text-primary focus:border-primary focus:outline-none transition-colors duration-fast"
                  />
                </div>
              </div>
            </div>

            {/* Product Catalog by Category */}
            <div className="space-y-6">
              <h2 className="font-heading text-2xl font-bold text-text-primary border-b border-border pb-3">
                Product Catalog
              </h2>

              {isLoading ? (
                <div className="text-center py-12">
                  <p className="font-body text-text-secondary">Loading products...</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {Object.keys(groupedProducts).map((category) => (
                    <div key={category} className="space-y-4">
                      {/* Category Header */}
                      <h3 className="font-heading text-xl font-bold text-primary bg-muted px-4 py-3 border-l-4 border-primary">
                        {category}
                      </h3>

                      {/* Category Products Table */}
                      <div className="overflow-x-auto border border-border">
                        <table className="w-full min-w-full">
                          <thead className="bg-muted">
                            <tr>
                              <th className="px-4 py-3 text-left font-cta text-sm font-semibold text-text-primary border-b border-border">
                                Part ID
                              </th>
                              <th className="px-4 py-3 text-left font-cta text-sm font-semibold text-text-primary border-b border-border">
                                Item Description
                              </th>
                              <th className="px-4 py-3 text-center font-cta text-sm font-semibold text-text-primary border-b border-border w-32">
                                Quantity
                              </th>
                              <th className="px-4 py-3 text-right font-cta text-sm font-semibold text-text-primary border-b border-border w-32">
                                Price
                              </th>
                              <th className="px-4 py-3 text-right font-cta text-sm font-semibold text-text-primary border-b border-border w-40">
                                Line Subtotal
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedProducts[category].map((product) => (
                              <tr key={product.id} className="border-b border-border hover:bg-muted transition-colors duration-fast">
                                <td className="px-4 py-3 font-body text-sm text-text-primary">
                                  {product.part_id}
                                </td>
                                <td className="px-4 py-3 font-body text-sm text-text-primary">
                                  {product.description}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <input
                                    type="number"
                                    min="0"
                                    value={product.quantity || ''}
                                    onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                                    className="w-20 px-2 py-1 text-center border border-border bg-background text-text-primary focus:border-primary focus:outline-none transition-colors duration-fast"
                                    placeholder="0"
                                  />
                                </td>
                                <td className="px-4 py-3 text-right font-body text-sm text-text-primary">
                                  ${product.price.toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right font-body text-sm font-semibold text-text-primary">
                                  ${calculateLineTotal(product.price, product.quantity).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}

                  {/* Grand Total Section */}
                  <div className="border-t-2 border-border pt-6">
                    <div className="flex justify-end items-center">
                      <div className="text-right">
                        <p className="font-cta text-base font-bold text-text-primary mb-2">
                          GRAND TOTAL:
                        </p>
                        <p className="font-cta text-3xl font-bold text-primary">
                          ${calculateGrandTotal().toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Additional Notes */}
            <div className="space-y-6">
              <h2 className="font-heading text-2xl font-bold text-text-primary border-b border-border pb-3">
                Additional Information
              </h2>

              <div>
                <label htmlFor="notes" className="block font-cta text-sm font-semibold text-text-primary mb-2">
                  NOTES / SPECIAL REQUIREMENTS
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={6}
                  placeholder="Please provide any additional details, special requirements, or questions about your order..."
                  className="w-full px-4 py-3 border border-border bg-background text-text-primary focus:border-primary focus:outline-none transition-colors duration-fast resize-vertical"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center pt-6">
              <button
                type="submit"
                disabled={isSubmitting || isLoading}
                className="px-12 py-4 bg-primary text-primary-foreground font-cta font-semibold text-sm tracking-wide border border-primary transition-all duration-fast hover:-translate-y-1 hover:bg-accent hover:border-accent disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitting ? 'SUBMITTING...' : 'SUBMIT ORDER'}
              </button>
            </div>
          </form>

          {/* Contact Information */}
          <div className="mt-16 p-8 bg-muted border border-border">
            <h3 className="font-heading text-xl font-bold text-text-primary mb-4">
              Need Immediate Assistance?
            </h3>
            <p className="font-body text-text-secondary mb-4">
              For urgent inquiries or if you prefer to speak with our team directly, please contact us:
            </p>
            <div className="space-y-2 font-body text-text-primary">
              <p>Phone: <a href="tel:+17708525050" className="text-primary hover:underline">+1(770)852-5050</a></p>
              <p>Email: <a href="mailto:sales@crelevator.com" className="text-primary hover:underline">sales@crelevator.com</a></p>
              <p>Business Hours: Monday - Friday, 8:00 AM - 4:00 PM</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}